"use client"

import * as React from 'react'
import { Page, PageBody } from '@open-mercato/ui/backend/Page'
import { CrudForm, type CrudField, type CrudFormGroup } from '@open-mercato/ui/backend/CrudForm'
import { apiCall } from '@open-mercato/ui/backend/utils/apiCall'
import { raiseCrudError } from '@open-mercato/ui/backend/utils/serverErrors'
import { useT } from '@open-mercato/shared/lib/i18n/context'

export default function CreateDocumentPage() {
  const t = useT()

  const fields = React.useMemo<CrudField[]>(() => [
    {
      id: 'documentType',
      label: t('documents.form.fields.documentType', 'Document Type'),
      type: 'select',
      required: true,
      options: [
        { value: 'CMR', label: t('documents.lookups.documentType.CMR', 'CMR') },
        { value: 'ECMR', label: t('documents.lookups.documentType.ECMR', 'eCMR') },
        { value: 'CIM', label: t('documents.lookups.documentType.CIM', 'CIM') },
        { value: 'SMGS', label: t('documents.lookups.documentType.SMGS', 'SMGS') },
        { value: 'MULTIMODAL', label: t('documents.lookups.documentType.MULTIMODAL', 'Multimodal') },
      ],
    },
    {
      id: 'documentNumber',
      label: t('documents.form.fields.documentNumber', 'Document Number'),
      type: 'text',
      required: true,
    },
    {
      id: 'cargoDescription',
      label: t('documents.form.fields.cargoDescription', 'Cargo Description'),
      type: 'textarea',
    },
    {
      id: 'weightKg',
      label: t('documents.form.fields.weightKg', 'Weight (kg)'),
      type: 'number',
    },
    {
      id: 'packagesCount',
      label: t('documents.form.fields.packagesCount', 'Packages Count'),
      type: 'number',
    },
  ], [t])

  const groups = React.useMemo<CrudFormGroup[]>(() => [
    {
      id: 'main',
      title: t('documents.pages.list.title', 'Transport Documents'),
      column: 1,
      fields: ['documentType', 'documentNumber', 'cargoDescription'],
    },
    {
      id: 'metrics',
      title: t('documents.form.fields.notes', 'Details'),
      column: 2,
      fields: ['weightKg', 'packagesCount'],
    },
  ], [t])

  const successRedirect = React.useMemo(
    () => `/backend/documents?flash=${encodeURIComponent(t('documents.messages.createSuccess', 'Document created successfully'))}&type=success`,
    [t],
  )

  return (
    <Page>
      <PageBody>
        <CrudForm
          title={t('documents.form.title.create', 'Create Transport Document')}
          backHref="/backend/documents"
          fields={fields}
          groups={groups}
          submitLabel={t('documents.actions.create', 'Create Document')}
          cancelHref="/backend/documents"
          successRedirect={successRedirect}
          onSubmit={async (values) => {
            const call = await apiCall('/api/freight/documents', {
              method: 'POST',
              body: JSON.stringify(values),
            })
            if (!call.ok) {
              await raiseCrudError(call.response, t('documents.messages.createError', 'Failed to create document'))
            }
          }}
        />
      </PageBody>
    </Page>
  )
}
